import { useEffect } from "react";
import {
  Users, ClipboardList, FileText, BarChart3, Upload,
  MessageSquare, Shield, Settings, LogOut, User,
  ChevronLeft, ChevronRight, AlertTriangle, Megaphone, X
} from "lucide-react";

const NAV_ITEMS = [
  { id: "dashboard", label: "STAFF", icon: Users, section: "main" },
  { id: "quota", label: "QUOTAS", icon: ClipboardList, section: "main", permKey: "quota_history" },
  { id: "reports", label: "REPORTS", icon: FileText, section: "tools", permKey: "reports" },
  { id: "analytics", label: "ANALYTICS", icon: BarChart3, section: "tools" },
  { id: "bulkimport", label: "IMPORT", icon: Upload, section: "tools" },
  { id: "feedback", label: "FEEDBACK", icon: MessageSquare, section: "tools", external: true },
];

const ADMIN_ITEMS = [
  { id: "admin", label: "ADMIN", icon: Settings, requiresBrittany: false, permKey: "admin_panel" },
  { id: "announce", label: "ANNOUNCE", icon: Megaphone, requiresBrittany: false, permKey: "announcements" },
  { id: "security", label: "SECURITY", icon: Shield, requiresBrittany: true },
];

const Sidebar = ({
  activeView,
  onViewChange,
  currentUser,
  userRole,
  discordId,
  permissions,
  flaggedCount,
  collapsed,
  onToggleCollapse,
  onLogout,
  mobileOpen = false,
  onMobileClose,
}) => {
  const isBrittany = discordId === "227845414378930176";

  const handleNavClick = (item) => {
    if (item.external) {
      window.open("/feedback", "_blank");
      if (onMobileClose) onMobileClose();
      return;
    }
    onViewChange(item.id);
    if (onMobileClose) onMobileClose();
  };

  const isVisible = (item) => {
    if (item.permKey && !permissions[item.permKey]) return false;
    if (item.requiresBrittany && !isBrittany) return false;
    return true;
  };

  const mainItems = NAV_ITEMS.filter(i => i.section === "main" && isVisible(i));
  const toolItems = NAV_ITEMS.filter(i => i.section === "tools" && isVisible(i));
  const adminItems = ADMIN_ITEMS.filter(i => isVisible(i));

  // Body scroll lock while the mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  // Close the drawer with Escape on mobile
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape" && onMobileClose) onMobileClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, onMobileClose]);

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
          aria-label="Close menu"
          className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-[100dvh] z-40 flex flex-col border-r border-zinc-800 bg-[#0A0A0A] transition-transform duration-200 ease-out ${
          collapsed ? "md:w-16" : "md:w-56"
        } w-64 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        data-testid="sidebar"
      >
      {/* Brand */}
      <div className="px-3 py-4 border-b border-zinc-800 flex items-center gap-2.5 min-h-[60px]">
        <img src="/logo.png" alt="Union Interactive" className="w-8 h-8 object-contain shrink-0" />
        {!collapsed && (
          <div className="overflow-hidden flex-1">
            <p className="font-mono text-xs font-bold text-white tracking-widest truncate">UNION</p>
            <p className="font-mono text-[10px] text-[#FFD600] font-bold tracking-wider truncate">INTERACTIVE</p>
          </div>
        )}
        {/* Mobile-only close button (44x44 tap target per Apple HIG) */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onMobileClose) onMobileClose();
          }}
          data-testid="sidebar-close-btn"
          aria-label="Close menu"
          className="md:hidden shrink-0 w-11 h-11 flex items-center justify-center text-zinc-400 active:bg-zinc-800 active:text-white transition-colors -mr-2"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Nav Sections */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-4">
        {/* Main */}
        <div>
          {!collapsed && <p className="text-[9px] font-mono text-zinc-600 uppercase tracking-[0.2em] px-2 mb-2">Main</p>}
          <div className="space-y-0.5">
            {mainItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  data-testid={`nav-${item.id}`}
                  className={`w-full flex items-center gap-3 px-2.5 py-2 text-sm font-mono transition-colors ${
                    isActive
                      ? "bg-[#00E5FF]/10 text-[#00E5FF] border-l-2 border-[#00E5FF]"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 border-l-2 border-transparent"
                  } ${collapsed ? "justify-center" : ""}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {!collapsed && <span className="text-xs font-semibold tracking-wide">{item.label}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tools */}
        <div>
          {!collapsed && <p className="text-[9px] font-mono text-zinc-600 uppercase tracking-[0.2em] px-2 mb-2">Tools</p>}
          <div className="space-y-0.5">
            {toolItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  data-testid={`nav-${item.id}`}
                  className={`w-full flex items-center gap-3 px-2.5 py-2 text-sm font-mono relative transition-colors ${
                    isActive
                      ? "bg-[#00E5FF]/10 text-[#00E5FF] border-l-2 border-[#00E5FF]"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 border-l-2 border-transparent"
                  } ${collapsed ? "justify-center" : ""}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {!collapsed && <span className="text-xs font-semibold tracking-wide">{item.label}</span>}
                  {item.id === "reports" && flaggedCount > 0 && (
                    <span className={`${collapsed ? "absolute top-0.5 right-0.5" : "ml-auto"} w-4 h-4 bg-[#FF3366] text-white text-[9px] font-bold flex items-center justify-center`}>
                      {flaggedCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Admin */}
        {adminItems.length > 0 && (
          <div>
            {!collapsed && <p className="text-[9px] font-mono text-zinc-600 uppercase tracking-[0.2em] px-2 mb-2">Admin</p>}
            <div className="space-y-0.5">
              {adminItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item)}
                    data-testid={`nav-${item.id}`}
                    className={`w-full flex items-center gap-3 px-2.5 py-2 text-sm font-mono transition-colors ${
                      isActive
                        ? "bg-[#00E5FF]/10 text-[#00E5FF] border-l-2 border-[#00E5FF]"
                        : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 border-l-2 border-transparent"
                    } ${collapsed ? "justify-center" : ""}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {!collapsed && <span className="text-xs font-semibold tracking-wide">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* User + Collapse */}
      <div className="border-t border-zinc-800 p-2 space-y-1">
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-1.5">
            <div className="w-6 h-6 bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <User className="w-3 h-3 text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-mono text-zinc-300 truncate">{currentUser}</p>
              <p className="text-[9px] font-mono text-zinc-600 uppercase">{userRole}</p>
            </div>
          </div>
        )}
        <div className="flex gap-1">
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className={`flex items-center justify-center gap-2 py-1.5 text-zinc-500 hover:text-[#FF3366] hover:bg-[#FF3366]/10 transition-colors ${
              collapsed ? "w-full px-2" : "flex-1 px-2"
            }`}
            title="Logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            {!collapsed && <span className="text-[10px] font-mono font-semibold uppercase">Logout</span>}
          </button>
          <button
            onClick={onToggleCollapse}
            data-testid="toggle-sidebar-btn"
            className="flex items-center justify-center px-2 py-1.5 text-zinc-600 hover:text-zinc-300 hover:bg-zinc-800/50 transition-colors"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
